import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DashboardCustomize } from '~/assets/svg/dashboard_customize_white_24dp.svg';
import { Modules } from '~/data/modules';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import {
  LegacyModuleNavigationItem,
  LegacyModuleNavigationItemConfig,
  LegacyUIModule,
  LegacyUiModuleFAIcon,
} from '~/legacy-components/LegacyNavigation';
import {
  getLegacyNavigationItem,
  resolveUndefinedModuleVersion,
} from '~/legacy-components/LegacyNavigation/utils';
import { legacyColors } from '~/legacyTheme';
import { useAppModules } from '~/scenes/AppModules/useAppModules';
import {
  EmployeeViewModules,
  getEmployeeModuleTabPath,
} from '~/scenes/PortalViews/Views/EmployeeViews/data';

export const useLegacyModuleNavigation = (): {
  navigationItems: LegacyModuleNavigationItem[];
  findNavigationItem: (
    module: LegacyUIModule,
  ) => LegacyModuleNavigationItem | undefined;
  isActiveModulePath: (module: LegacyUIModule, path: string) => boolean;
  isLoading: boolean;
} => {
  const { t } = useTranslation();
  const { isModuleActive } = useAppModules();
  const {
    selectedEmployeeId,
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: {
      isInMyUnits,
      isLoading: isLoadingSelectedEmployeeDetails,
      isDataInSync,
    },
  } = useSelectedEmployeeId();
  const isLoading = isLoadingSelectedEmployeeDetails || !isDataInSync;

  const hasAccessToEmployeeModule =
    isInMyUnits || isSelectedEmployeeLoggedInUser;

  const getEmployeeModulePath = useCallback(
    (module: EmployeeViewModules) => {
      return getEmployeeModuleTabPath(selectedEmployeeId, module);
    },
    [selectedEmployeeId],
  );

  const defaultConfigs: LegacyModuleNavigationItemConfig[] = useMemo(() => {
    return [
      {
        module: LegacyUIModule.Workspace,
        title: t('laWorkspace'),
        customIcon: <DashboardCustomize />,
        legacyUI: { path: '/v2/portal/my-workspace' },
        legacyV2: { path: '/portal/my-workspace' },
        hidden: !isModuleActive(Modules.WORKSPACE),
      },
      {
        module: LegacyUIModule.Home,
        title: t('laHome'),
        faIcon: LegacyUiModuleFAIcon.Home,
        legacyUI: { path: '/v2/portal/home' },
        legacyV2: { path: '/portal/home' },
      },
      {
        module: LegacyUIModule.People,
        title: t('laPeople'),
        faIcon: LegacyUiModuleFAIcon.People,
        color: legacyColors.modules.people,
        legacyUI: {
          path: hasAccessToEmployeeModule
            ? '/page-people.html'
            : '/page-personalEmp.html',
        },
        legacyV2: {
          path: getEmployeeModulePath(EmployeeViewModules.People),
        },
      },
      {
        module: LegacyUIModule.Rewarding,
        title: t('laRewarding'),
        faIcon: LegacyUiModuleFAIcon.Rewarding,
        color: legacyColors.modules.rewarding,
        legacyUI: { path: '/page-rewarding.html' },
        legacyV2: {
          path: getEmployeeModulePath(EmployeeViewModules.Rewarding),
        },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Performance,
        title: t('laPerformance'),
        faIcon: LegacyUiModuleFAIcon.Performance,
        color: legacyColors.modules.performance,
        legacyUI: { path: '/page-performance.html' },
        legacyV2: {
          path: getEmployeeModulePath(EmployeeViewModules.Performance),
        },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Development,
        title: t('laDevelopment'),
        faIcon: LegacyUiModuleFAIcon.Development,
        color: legacyColors.modules.development,
        legacyUI: { path: '/page-development.html' },
        legacyV2: {
          path: getEmployeeModulePath(EmployeeViewModules.Development),
        },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Analytics,
        title: t('laAnalytics'),
        faIcon: LegacyUiModuleFAIcon.Analytics,
        color: legacyColors.modules.analytics,
        legacyUI: { path: '/page-analytics.html' },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Collaboration,
        title: t('laCollaboration'),
        faIcon: LegacyUiModuleFAIcon.Collaboration,
        color: legacyColors.modules.collaboration,
        legacyUI: { path: '/page-collaboration.html' },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Organization,
        title: t('laOrganization'),
        faIcon: LegacyUiModuleFAIcon.Organization,
        color: legacyColors.modules.organization,
        legacyUI: { path: '/page-organization.html' },
      },
      {
        module: LegacyUIModule.Recruiting,
        title: t('laRecruiting'),
        faIcon: LegacyUiModuleFAIcon.Recruiting,
        color: legacyColors.modules.recruiting,
        legacyUI: { path: '/page-recruiting.html' },
        disabled: !hasAccessToEmployeeModule,
      },
      {
        module: LegacyUIModule.Planning,
        title: t('laPlanning'),
        faIcon: LegacyUiModuleFAIcon.Planning,
        color: legacyColors.modules.planning,
        legacyUI: { path: '/page-planning.html' },
        disabled: !hasAccessToEmployeeModule,
      },
    ];
  }, [t, isModuleActive, hasAccessToEmployeeModule, getEmployeeModulePath]);

  const resolveModuleVersion = (
    config: LegacyModuleNavigationItemConfig,
  ): LegacyModuleNavigationItemConfig => {
    // TODO: Set module versions by config settings, by default use newest available.
    return resolveUndefinedModuleVersion(config);
  };

  const configs = useMemo(
    () => defaultConfigs.map((c) => resolveModuleVersion(c)),
    [defaultConfigs],
  );
  const items = useMemo(
    () => configs.map((c) => getLegacyNavigationItem(c, selectedEmployeeId)),
    [configs, selectedEmployeeId],
  );

  const findNavigationConfig = (
    module: LegacyUIModule,
  ): LegacyModuleNavigationItemConfig | undefined => {
    return configs.find((i) => i.module === module);
  };

  const findNavigationItem = (
    module: LegacyUIModule,
  ): LegacyModuleNavigationItem | undefined => {
    return items.find((i) => i.module === module);
  };

  const isActiveModulePath = (module: LegacyUIModule, path: string) => {
    const config = findNavigationConfig(module);
    if (config && config.legacyV2) {
      const modulePath = config.legacyV2.path;
      return path.startsWith(modulePath);
    }

    return false;
  };

  return {
    navigationItems: items,
    findNavigationItem: findNavigationItem,
    isActiveModulePath: isActiveModulePath,
    isLoading,
  };
};
